import React, { useState } from 'react';
import { Flex } from 'rebass';
import {
  FormComboGroup,
  Input,
  SearchInput,
  Select,
} from '../../../../components/Form';
import Button from '../../../../components/Button/Button';
import PropTypes from 'prop-types';
import { isBrowser } from 'react-device-detect';
import ReactSelect, {
  formatDotLabel,
  formatDotStyles,
} from '../../../../components/ReactSelect';
import colors from '../../../../theme/colors';
import { FilterBarProps } from '../../../../components/FilterBar/FilterBar';
import { rebassProps } from '../../../../theme/theme';
import { BackflowTestFlagsPicker } from '../../../../components/BackflowTest/BackflowTestFlagsPicker';
import { MonthDayPicker } from '../../../../components/Date/MonthDayPicker';
import { getStringSearchPlaceholder } from '../../../../helpers/search';

export const statusOptions = [
  {
    value: 'NEEDS_APPROVAL',
    label: 'Needs Approval',
    color: colors.statusPending,
  },
  {
    value: 'APPROVED',
    label: 'Approved',
    color: colors.statusSuccess,
  },
  {
    value: 'REJECTED',
    label: 'Rejected',
    color: colors.statusError,
  },
  {
    value: 'UNSUBMITTED',
    label: 'Unsubmitted',
    color: colors.statusPrimary,
  },
] as const;

// const resultOptions = [
//   {
//     value: 'ALL',
//     label: '- result -',
//   },
//   {
//     value: 'PASS',
//     label: 'Pass',
//   },
//   {
//     value: 'FAIL',
//     label: 'Fail',
//   },
//   {
//     value: 'FAILPASS',
//     label: 'Fail/Pass',
//   },
// ]

export const stringSearchFields = [
  {
    field: 'jobId',
    label: 'Job',
  },
  {
    field: 'job.address.street1',
    label: 'Address',
  },
  {
    field: 'job.address.street2',
    label: null,
  },
  {
    field: 'backflowAssembly.waterService.siteNumber',
    label: 'Service',
  },
  {
    field: 'backflowAssembly.serialNumber',
    label: 'Serial',
  },
  {
    field: 'backflowAssembly.waterService.pin',
    label: 'TRAC',
  },
  {
    field: 'testingCompanyName',
    label: 'Company',
  },
  {
    field: 'testerName',
    label: 'Tester',
  },
] as const;

export const dateOptions = [
  {
    field: '',
    label: '- date -',
    defaultOperator: undefined,
  },
  {
    field: 'dateTested',
    label: 'Tested',
  },
  {
    field: 'testingCompanySubmitted',
    label: 'Submitted',
  },
] as const;

export type TestReportFilters = {
  waterSupplierStatus?: string[];
  testResult?: string;
  stringSearch?: string;
  currentDateFilterField?: (typeof dateOptions)[number]['field'];
  dateStart?: string;
  dateEnd?: string;
  flags?: string[];
};

export const defaultFilters: TestReportFilters = {
  waterSupplierStatus: ['NEEDS_APPROVAL'],
};

type FilterBarTestReportProps = FilterBarProps<TestReportFilters>;

const FilterBarTestReport: React.FC<FilterBarTestReportProps> = (props) => {
  const { filters, setFilters, selectedId } = props;

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFilters((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const [dateAutoFocus] = useState(!filters.currentDateFilterField);

  return (
    <Flex variant="filterBar.container" {...(props as rebassProps)}>
      <Flex variant="filterBar.left">
        <SearchInput
          placeholder={getStringSearchPlaceholder(stringSearchFields)}
          autoFocus={isBrowser && !selectedId}
          width={350}
          name="stringSearch"
          value={filters.stringSearch || ''}
          onChange={onChange}
        />
        <ReactSelect
          placeholder="approval"
          isMulti
          isClearable={false}
          styles={{
            menu: (base) => ({
              ...base,
              minWidth: '190px',
            }),
            multiValue: formatDotStyles,
          }}
          formatOptionLabel={formatDotLabel}
          onChange={(values) => {
            if (Array.isArray(values)) {
              setFilters({
                ...filters,
                waterSupplierStatus:
                  values.length > 0
                    ? values.map((value1) => value1.value)
                    : undefined,
              });
            }
          }}
          value={
            Array.isArray(filters.waterSupplierStatus)
              ? filters.waterSupplierStatus.map((status) =>
                  statusOptions.find(
                    (statusOption) => statusOption.value === status
                  )
                )
              : ([] as any[])
          }
          options={statusOptions}
        />
        {/*<Select*/}
        {/*  ml={3}*/}
        {/*  value={filters.testResult}*/}
        {/*  name="testResult"*/}
        {/*  onChange={onChange}*/}
        {/*>*/}
        {/*  {resultOptions.map((result, i) => (*/}
        {/*    <option value={result.value} key={i}>*/}
        {/*      {result.label}*/}
        {/*    </option>*/}
        {/*  ))}*/}
        {/*</Select>*/}
        <BackflowTestFlagsPicker
          value={filters.flags}
          onChange={(values) => {
            if (Array.isArray(values)) {
              setFilters({
                ...filters,
                flags:
                  values.length > 0
                    ? values.map((value1) => value1.value)
                    : undefined,
              });
            }
          }}
        />
        <FormComboGroup>
          <Select
            name="currentDateFilterField"
            value={filters.currentDateFilterField || dateOptions[0].field}
            onChange={onChange}
            variant={filters.currentDateFilterField ? 'selectCombo' : 'select'}
            width={120}
          >
            {dateOptions.map((dateOption, i) => (
              <option value={dateOption.field} key={i}>
                {dateOption.label}
              </option>
            ))}
          </Select>
          {filters.currentDateFilterField && (
            <MonthDayPicker
              startDate={filters.dateStart}
              endDate={filters.dateEnd}
              autoFocus={dateAutoFocus}
              variant="inputRoundedRight"
              onChange={(dateStart, dateEnd) => {
                setFilters((prevState) => ({
                  ...prevState,
                  dateStart: dateStart ?? undefined,
                  dateEnd: dateEnd ?? undefined,
                }));
              }}
            />
          )}
        </FormComboGroup>
      </Flex>

      <Flex variant="filterBar.right">
        <Button
          ml={3}
          variant="outline"
          icon="undo"
          onClick={() => setFilters(defaultFilters)}
          title="Reset Form"
        />
      </Flex>
    </Flex>
  );
};

FilterBarTestReport.propTypes = {
  filters: PropTypes.any,
  setFilters: PropTypes.func.isRequired,
};

export default FilterBarTestReport;
